import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { APP_ROUTES } from '@models/shared/route/all-routes';
import { AlertService } from './alert.service';
import { StaticDataService } from './static-data.service';

@Injectable({
  providedIn: 'root'
})
export class RouteDataService {
  private alertService = inject(AlertService);
  private staticService = inject(StaticDataService);
  private router = inject(Router);
  private msgInvalid = "Invalid request, try again";

  // ==========================================
  // 🔷 1. GENERIC NAVIGATION & HELPERS
  // ==========================================

  routeTo(routeName: APP_ROUTES) {
    this.router.navigate([routeName]);
  }

  private getPlaceHolder(url: string, placeholder: string): string {
    return url.replace('#placeholder', placeholder);
  }

  // ==========================================
  // 🔷 2. DASHBOARD / CORE SCREENS
  // ==========================================

  goToSupervisorDashboard() {
    this.router.navigate(['/dashboard/supervisor']);
  }

  goToCustomerAccounts() {
    this.router.navigate(['/dashboard/customer-accounts']);
  }

  goToVendorAccounts() {
    this.router.navigate(['/dashboard/vendor-accounts']);
  }

  // ==========================================
  // 🔷 3. PRODUCTS & INVENTORY
  // ==========================================

  routeToProduct(elementLocalCode: string | undefined) {
    this.router.navigate([APP_ROUTES.ORDER_PRODUCT + elementLocalCode]);
  }

  routeCreateProduct(elementLocalCode: string | undefined) {
    this.router.navigate([APP_ROUTES.ORDER_PRODUCT_CREATE + elementLocalCode]);
  }

  routeEditProduct(elementLocalCode: string | undefined, product_id: number) {
    this.router.navigate([APP_ROUTES.ORDER_PRODUCT_UPDATE + elementLocalCode], {
      queryParams: { id: product_id },
    });
  }

  routeDeleteProduct(elementLocalCode: string | undefined, product_id: number) {
    this.router.navigate([APP_ROUTES.ORDER_PRODUCT_UPDATE + elementLocalCode], {
      queryParams: {
        id: product_id,
        delete: true
      },
    });
  }

  // ==========================================
  // 🔷 4. ORDERS (SALES & PURCHASES)
  // ==========================================

  routeToOrder(elementLocalCode: string | undefined) {
    if (elementLocalCode) {
      const url = this.getPlaceHolder(APP_ROUTES.CREATE_ORDER, elementLocalCode);
      this.router.navigate([url]);
    }
  }

  createOrder(elementLocalCode: string | undefined, contact?: any) {
    if (elementLocalCode) {
      const url = this.getPlaceHolder(APP_ROUTES.CREATE_ORDER, elementLocalCode);
      const queryParams: any = contact ? { contact_id: contact.contact_id } : {};
      this.router.navigate([url], { queryParams: queryParams });
    }
  }

  viewOrder(order: any) {
    const url = this.getPlaceHolder(APP_ROUTES.VIEW_ORDER+ '/' + order.order_id, order.element_local_code);
    this.router.navigate([url]);
  }

  editOrder(order: any) {
    const url = this.getPlaceHolder(APP_ROUTES.UPDATE_ORDER, order.element_local_code);
    this.router.navigate([url], { queryParams: { id: order.order_id } });
  }

  viewAllOrders(elementLocalCode: string | undefined) {
    if (elementLocalCode) {
      this.router.navigate([`order/${elementLocalCode}/view-all`]);
    }
  }

  viewAllOrderAccounts() {
    this.router.navigate([APP_ROUTES.VIEWALL_ORDER_ACCOUNTS]);
  }

  // ==========================================
  // 🔷 5. CONTACTS & HRM
  // ==========================================

  viewContact(contact: any) {
    if (contact?.contact_type) {
      this.router.navigate([`/hrm/${contact.contact_type?.contact_type_code}/view/${contact.contact_id}`]);
    } else if (contact?.contact_type_id) {
      const contactType = this.staticService.getContactTypeByID(contact.contact_type_id);
      if (contactType?.contact_type_code) {
        this.router.navigate([`/hrm/${contactType?.contact_type_code}/view/${contact.contact_id}`]);
      } else {
        this.alertService.error(this.msgInvalid);
      }
    } else {
      this.alertService.error(this.msgInvalid);
    }
  }

  goToClosureRequests() {
    this.router.navigate(['/hrm/closure-requests']);
  }

  createContact(contactTypeCd: string | undefined) {
    if (contactTypeCd) {
      this.router.navigate([`/hrm/${contactTypeCd}/create`]);
    } else {
      this.router.navigate([`/hrm/contacts/create`]);
    }
  }

  updateContact(contact: any) {
    if (contact && contact.contact_type) {
      this.router.navigate([`/hrm/${contact.contact_type?.contact_type_code}/update/${contact.contact_id}`]);
    } else {
      this.alertService.error(this.msgInvalid)
    }
  }

  newCustomer() {
    this.router.navigate([`/hrm/customer/create`]);
  }

  newContact(type: any) {
    this.router.navigate([`/hrm/${type}/create`]);
  }

  viewAllContacts(type: any) {
    this.router.navigate([`hrm/${type}/view-all`]);
  }

  viwCustomers() {
    this.router.navigate([`hrm/contacts/view-all`], { queryParams: { contact_type_id: 1 } });
  }

  // ==========================================
  // 🔷 6. WALLETS & ACCOUNTS
  // ==========================================

  viewWallet(wallet: any) {
    this.router.navigate([APP_ROUTES.WALLET_VIEW.replace(':id', wallet?.wallet_id?.toString() || '')]);
  }

  viewAllWallets() {
    this.router.navigate([APP_ROUTES.WALLET_VIEWALL]);
  }

  viewMyWallets() {
    this.router.navigate([APP_ROUTES.WALLET_VIEWALL], { queryParams: {isMyWallets: true} });
  }

  transferWallet(sourceWalletId?: number) {
    const queryParams: any = { action: 'transfer' };
    if (sourceWalletId) {
      queryParams['wallet_id'] = sourceWalletId;
    }
    this.router.navigate([APP_ROUTES.WALLET_VIEWALL], { queryParams });
  }

  // ==========================================
  // 🔷 7. TRANSACTIONS & PAYMENTS
  // ==========================================

  createTransaction(contactType?: string, contact?: any, wallet?: any) {
    const queryParams: any = {};
    if (contactType) {
      queryParams['contact_type'] = contactType;
    }
    
    if (contact) {
      queryParams['contact_id'] = contact.contact_id;
    }
    if (wallet) {
      queryParams['wallet_id'] = wallet.wallet_id;
    }
    
    this.router.navigate([APP_ROUTES.CREATE_TRANSACTION], { queryParams });
  }

  viewTransaction(transaction: any) {
    const cType = transaction.contact_type?.contact_type_code;
    const id = transaction.payment_id;
    if (id) {
      this.router.navigate([
        APP_ROUTES.VIEW_TRANSACTION.replace(':id', String(id))
      ], { queryParams: { contact_type: cType } });
    } else {
      this.alertService.error(this.msgInvalid);
    }
  }

  updateTransaction(transaction: any) {
    console.log(transaction,'transaction to update');
    const id = transaction.payment_id;
    if (id) {
      this.router.navigate([APP_ROUTES.UPDATE_TRANSACTION.replace(':id', String(id))]);
    } else {
      this.alertService.error(this.msgInvalid);
    }
  }

  allTransactions(contactType?: any) {
    const queryParams: any = {};
    if (contactType) {
      queryParams['contact_type'] = contactType;
    }
    this.router.navigate([APP_ROUTES.VIEWALL_TRANSACTION], { queryParams });
  }

  // ==========================================
  // 🔷 8. WORKSHEETS
  // ==========================================

  viewAllWorksheet(workgroup: string) {
    this.router.navigate([APP_ROUTES.VIEWALL_WORKSHEET.replace(':workgroup', workgroup)]);
  }

  createWorksheet(workgroup: string, contact?: any) {
    const queryParams: any = contact ? { contact_id: contact.contact_id } : {};
    this.router.navigate([APP_ROUTES.CREATE_WORKSHEET.replace(':workgroup', workgroup)], { queryParams: queryParams });
  }

  updateWorkSheet(workgroup: string, worksheet: any) {
    this.router.navigate([`${APP_ROUTES.UPDATE_WORKSHEET.replace(':workgroup', workgroup)}/${worksheet.worksheet_id}`]);
  }

  viewWorkSheet(workgroup: string, worksheet: any) {
    this.router.navigate([`${APP_ROUTES.VIEW_WORKSHEET.replace(':workgroup', workgroup)}/${worksheet.worksheet_id}`]);
  }

  // ==========================================
  // 🔷 9. WORK REPORTS
  // ==========================================

  createWorkreport(workgroup: string) {
    this.router.navigate([`${APP_ROUTES.CREATE_WORKREPORT.replace(':workgroup', workgroup)}`]);
  }

  viewAllWorkreport(workgroup: string) {
    this.router.navigate([APP_ROUTES.VIEWALL_WORKREPORT.replace(':workgroup', workgroup)]);
  }

  viewWorkreport(workgroup: string, report: any) {
    this.router.navigate([`${APP_ROUTES.VIEW_WORKREPORT.replace(':workgroup', workgroup)}/${report.workreport_id}`]);
  }

  updateWorkreport(workgroup: string, workreport: any) {
    this.router.navigate([`${APP_ROUTES.UPDATE_WORKREPORT.replace(':workgroup', workgroup)}/${workreport.workreport_id}`]);
  }

  // ==========================================
  // 🔷 10. SETTINGS / CONFIGURATION / TAGS
  // ==========================================

  viewExpenseCategories() {
    this.router.navigate([APP_ROUTES.MANAGE_EXPENSES]);
  }

  goToManageTags() {
    this.router.navigate([APP_ROUTES.MANAGE_TAGS]);
  }

  goToManageExpenses() {
    this.router.navigate([APP_ROUTES.MANAGE_EXPENSES]);
  }

  goToManageDevices() {
    this.router.navigate([APP_ROUTES.MANAGE_DEVICES]);
  }

  goToRemindersHub() {
    this.router.navigate([APP_ROUTES.HRM_REMINDERS]);
  }
}
